import React, { useEffect, useState } from "react";
import {
  Table,
  Input,
  Button,
  Space,
  Popconfirm,
  message,
  Tag,
  Radio,
  Tooltip,
  Select,
  Popover,
  Modal
} from "antd";
import { Helmet } from "react-helmet";
import startCase from "lodash/startCase";
import capitalize from "lodash/capitalize";
import { useLocation, useNavigate } from "react-router-dom";
import { Typography } from "antd";
import {
  CheckOutlined,
  DeleteOutlined,
  EditOutlined,
  SyncOutlined,
  FilterOutlined,
  ExclamationCircleOutlined,
  CloudServerOutlined,
  EyeOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import MediaForm from "../components/Library/MediaForm";
import axios from "axios";
import dayjs from "dayjs";
import Link from "antd/es/typography/Link";
import { getMediaIcon, formatDuration } from "../utils/Misc";
import PreviewMedia from "../components/Library/PreviewMedia";
import RejectButton from "../components/Common/RejectButton";
import RequirePermission from "../components/Common/RequirePermission";

const { Title } = Typography;
const { Option } = Select;

const MANUAL_OPTIMIZE_THRESHOLD_BYTES = 30 * 1024 * 1024;

function isEligibleForManualOptimize(record) {
  if (!record.file_data || !["image", "video"].includes(record.media_type)) return false;
  let fileData;
  try {
    fileData = JSON.parse(record.file_data);
  } catch (e) {
    return false;
  }
  if (fileData.optimization_status === "processing") return false;

  const currentBytes = Number(fileData.optimized_file_size) || Number(fileData.original_file_size);
  const kb = parseFloat(fileData.file_size);
  // Fall back to the displayed file_size ("<KB> KB" string) when neither
  // byte-count field is present yet (very old rows uploaded before either
  // was tracked).
  const sizeBytes = currentBytes || (!Number.isNaN(kb) ? kb * 1024 : 0);
  const isOversized = sizeBytes > MANUAL_OPTIMIZE_THRESHOLD_BYTES;

  const notOptimized = fileData.optimization_status !== "optimized";

  return isOversized || notOptimized;
}

function isEligibleForYoutubeDurationCalc(record) {
  return record.media_type === "youtube" && !record.duration_seconds;
}

function formatBytes(bytes) {
  if (!bytes) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / Math.pow(1024, exponent);
  return `${value.toFixed(exponent === 0 ? 0 : 2)} ${units[exponent]}`;
}

const Library = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(window.location.search);
  const initialStatus = queryParams.get("status") || "1";
  const initialActiveStatus = queryParams.get("activeStatus") || "";
  const initialType = queryParams.get("type") || "";
  const initialOrientation = queryParams.get("orientation") || "";
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [media, setMedia] = useState([]);
  const [editingMedia, setEditingMedia] = useState(null);
  const [previewMedia, setPreviewMedia] = useState(null);
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(null);
  const [approvePermission, setApprovePermission] = useState(false);
  const [createPermission, setCreatePermission] = useState(false);
  const [editPermission, setEditPermission] = useState(false);
  const [deletePermission, setDeletePermission] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(initialStatus);
  const [activeStatus, setActiveStatus] = useState(initialActiveStatus);
  const [selectedType, setSelectedType] = useState(initialType);
  const [selectedOrientation, setSelectedOrientation] = useState(initialOrientation);
  const [syncLoading, setSyncLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(
    Boolean(initialActiveStatus || initialType || initialOrientation)
  );

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pendingTotal, setPendingTotal] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [filterText, setFilterText] = useState("");

  const [sortField, setSortField] = useState("id");
  const [sortOrder, setSortOrder] = useState("desc");
  const [isCleanupModalOpen, setIsCleanupModalOpen] = useState(false);
  const [cleanupScanning, setCleanupScanning] = useState(false);
  const [cleanupDeleting, setCleanupDeleting] = useState(false);
  const [orphanedResult, setOrphanedResult] = useState(null);
  const [selectedOrphanedKeys, setSelectedOrphanedKeys] = useState([]);
  const [orphanedPreview, setOrphanedPreview] = useState(null);
  const [orphanedPage, setOrphanedPage] = useState(1);
  const [orphanedPageSize, setOrphanedPageSize] = useState(10);
  const setQueryParams = (params) => {
    const searchParams = new URLSearchParams(params);
    navigate(`?${searchParams.toString()}`, { replace: true });
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    setFilterText(value);
    setCurrentPage(1);
  };

  const handleStatusChange = (value) => {
    setActiveStatus(value);
    const newQueryParams = {
      status: selectedStatus,
      activeStatus: value,
      type: selectedType,
      orientation: selectedOrientation,
    };
    setQueryParams(newQueryParams);
    setCurrentPage(1);
  };

  const handleTypeChange = (value) => {
    setSelectedType(value);
    const newQueryParams = {
      status: selectedStatus,
      activeStatus: activeStatus,
      type: value,
      orientation: selectedOrientation,
    };
    setQueryParams(newQueryParams);
    setCurrentPage(1);
  };

  const handleOrientationChange = (value) => {
    setSelectedOrientation(value || "");
    const newQueryParams = {
      status: selectedStatus,
      activeStatus: activeStatus,
      type: selectedType,
      orientation: value,
    };
    setQueryParams(newQueryParams);
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setActiveStatus("");
    setSelectedType("");
    setSelectedOrientation("");
    setCurrentPage(1);
    const newQueryParams = {
      status: selectedStatus,
      activeStatus: "",
      type: "",
      orientation: "",
    };
    setQueryParams(newQueryParams);
  };

  const handleTableChange = (pagination, filters, sorter) => {
    if (typeof sorter.order === "undefined") {
      setSortField("id");
      setSortOrder("desc");
    } else {
      const field = sorter.field === "file_data" ? "file_size" : sorter.field;
      setSortField(field);
      setSortOrder(sorter.order === "ascend" ? "asc" : "desc");
    }
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);
  };

  const handleEdit = (record) => {
    setIsModalOpen(true);
    setEditingMedia(record);
  };

  const handlePreviewMedia = (record) => {
    setIsPreviewModalOpen(true);
    setPreviewMedia(record);
  };

  const handleDelete = async (id) => {
    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_BASE_URL}/api/media/${id}`
      );
      const updated_row_count = response.data.data.updated_row_count;
      if (updated_row_count === 1) {
        message.success("Media deleted");
        await fetchData();
      }
    } catch (err) {
      console.log(err);
      message.error(err?.response?.data?.message || "Failed to delete media");
    }
  };

  const handleApprove = async (id) => {
    try {
      const response = await axios.patch(
        `${process.env.REACT_APP_BASE_URL}/api/media/approve/${id}`
      );
      const updated_row_count = response.data.data.updated_row_count;
      if (updated_row_count === 1) {
        message.success("Media approved");
        await fetchData();
      }
    } catch (err) {
      console.log(err);
    }
  };

  const handleReject = async (id, reason) => {
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_BASE_URL}/api/media/reject/${id}`,
        {
          reason: reason,
        }
      );
      const updated_row_count = response.data.data.updated_row_count;
      if (updated_row_count === 1) {
        message.success("Media rejected");
        await fetchData();
      }
    } catch (err) {
      console.log(err);
    }
  };

  const [optimizingId, setOptimizingId] = useState(null);

  const handleOptimize = async (id) => {
    try {
      setOptimizingId(id);
      await axios.post(
        `${process.env.REACT_APP_BASE_URL}/api/media/${id}/optimize`
      );
      message.success("Media optimized");
      await fetchData();
    } catch (err) {
      console.log(err);
      message.error(err?.response?.data?.message || "Failed to optimize media");
    } finally {
      setOptimizingId(null);
    }
  };

  const [calculatingDurationId, setCalculatingDurationId] = useState(null);

  const handleCalculateYoutubeDuration = async (id, urlData) => {
    try {
      setCalculatingDurationId(id);

      const lookup = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/youtube-duration`,
        { params: { url: urlData } }
      );
      if (!lookup.data.success) {
        throw new Error(lookup.data.message || "Failed to calculate duration");
      }
      await axios.post(
        `${process.env.REACT_APP_BASE_URL}/api/media/${id}/calculate-youtube-duration`,
        { duration_seconds: lookup.data.data.duration_seconds }
      );
      message.success("Video duration calculated");
      await fetchData();
    } catch (err) {
      console.log(err);
      message.error(err?.response?.data?.message || err.message || "Failed to calculate duration");
    } finally {
      setCalculatingDurationId(null);
    }
  };

  const handleSync = async () => {
    try {
      setSyncLoading(true);
      await axios.put(
        `${process.env.REACT_APP_BASE_URL}/api/media/sync`
      );
      message.success("Media synced successfully");
      await fetchData();
      setSyncLoading(false);
    } catch (err) {
      console.log(err);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        process.env.REACT_APP_BASE_URL + `/api/media`,
        {
          params: {
            filter: filterText, // add filter if needed
            limit: pageSize,
            page: currentPage,
            status: selectedStatus,
            activeStatus: activeStatus,
            type: selectedType,
            orientation: selectedOrientation,
            orderField: sortField,
            order: sortOrder,
          },
        }
      );
      let records = response.data.data.data;
      setTotalRecords(response.data.data.total);
      setPendingTotal(response.data.data.pending_total);
      setMedia(records);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error("Error fetching media data", error);
    }
  };

  const handleScanOrphaned = async () => {
    try {
      setCleanupScanning(true);
      setIsCleanupModalOpen(true);
      setSelectedOrphanedKeys([]);
      setOrphanedPage(1);
      const response = await axios.get(
        process.env.REACT_APP_BASE_URL + `/api/media/cleanup/scan-orphaned`
      );
      setOrphanedResult(response.data.data);
    } catch (error) {
      message.error("Failed to scan S3 storage");
      console.error("Error scanning orphaned media", error);
    } finally {
      setCleanupScanning(false);
    }
  };

  // Shared by both "Delete All" and "Delete Selected" - only the keys/
  // wording differ, the confirm-then-call-then-refresh flow is identical.
  const deleteOrphanedKeys = (keys, { allDeleted }) => {
    if (keys.length === 0) return;
    Modal.confirm({
      title: allDeleted ? "Delete all orphaned files?" : "Delete selected files?",
      icon: <ExclamationCircleOutlined />,
      content: `This will permanently delete ${keys.length} file(s) from S3 (environment: ${orphanedResult.prefix}). This cannot be undone.`,
      okText: allDeleted ? "Yes, delete all" : "Yes, delete selected",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          setCleanupDeleting(true);
          const response = await axios.post(
            process.env.REACT_APP_BASE_URL + `/api/media/cleanup/delete-orphaned`,
            { keys }
          );
          message.success(`Deleted ${response.data.data.deletedCount} file(s)`);
          if (allDeleted) {
            setIsCleanupModalOpen(false);
            setOrphanedResult(null);
          } else {
            const deletedKeys = new Set(response.data.data.deletedKeys);
            let remainingCount = 0;
            setOrphanedResult((prev) => {
              const remaining = prev.orphaned.filter((o) => !deletedKeys.has(o.key));
              remainingCount = remaining.length;
              return {
                ...prev,
                orphaned: remaining,
                orphanedCount: prev.orphanedCount - deletedKeys.size,
                orphanedBytes:
                  prev.orphanedBytes -
                  prev.orphaned
                    .filter((o) => deletedKeys.has(o.key))
                    .reduce((sum, o) => sum + o.size, 0),
              };
            });
            setSelectedOrphanedKeys((prev) => prev.filter((key) => !deletedKeys.has(key)));
            // Clamp the current page down if deleting emptied out the last
            // page(s) - otherwise the table would render blank instead of
            // falling back to a page that still has rows.
            setOrphanedPage((prevPage) => {
              const maxPage = Math.max(1, Math.ceil(remainingCount / orphanedPageSize));
              return Math.min(prevPage, maxPage);
            });
          }
        } catch (error) {
          message.error("Failed to delete files");
          console.error("Error deleting orphaned media", error);
        } finally {
          setCleanupDeleting(false);
        }
      },
    });
  };

  const handleDeleteOrphaned = () => {
    if (!orphanedResult || orphanedResult.orphaned.length === 0) return;
    deleteOrphanedKeys(orphanedResult.orphaned.map((o) => o.key), { allDeleted: true });
  };

  const handleDeleteSelectedOrphaned = () => {
    deleteOrphanedKeys(selectedOrphanedKeys, { allDeleted: false });
  };

  const handleApprovalStatus = (e) => {
    const newStatus = e.target.value;
    setSelectedStatus(newStatus);
    const newQueryParams = {
      status: newStatus,
    };
    setQueryParams(newQueryParams);
    setCurrentPage(1);
  };

  useEffect(() => {
    const newQueryParams = {
      status: selectedStatus,
      activeStatus: activeStatus,
      type: selectedType,
      orientation: selectedOrientation,
    };

    if (JSON.stringify(newQueryParams) !== JSON.stringify(Object.fromEntries(new URLSearchParams(location.search)))) {
      setQueryParams(newQueryParams);
    };

    setQueryParams(newQueryParams);
    const fetchTimer = setTimeout(() => {
      fetchData();
    }, 500);

    return () => clearTimeout(fetchTimer);
  }, [currentPage, pageSize, filterText, selectedStatus, activeStatus, selectedType, selectedOrientation, sortField, sortOrder]);

  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem("userData"));
    setUser(storedUser);
    const permissions = storedUser.permissions;
    setApprovePermission(parseInt(permissions["signage.media_approve"]) === 1);
    setCreatePermission(parseInt(permissions["signage.media_create"]) === 1);
    setDeletePermission(parseInt(permissions["signage.media_delete"]) === 1);
    setEditPermission(parseInt(permissions["signage.media_edit"]) === 1);
  }, []);

  const popOverColumns = [
    {
      title: "Id",
      dataIndex: "id",
      render: (text) => <span className="text-small">{startCase(text)}</span>,
    },
    {
      title: "Name",
      dataIndex: "name",
      render: (text) => <span className="text-small">{text?.toUpperCase()}</span>,
    },
  ];

  const columns = [
    { title: "Title", dataIndex: "title", sorter: true },
    {
      title: "Media",
      dataIndex: "media_type",
      render: (text, record) => (
        <Link href="#" onClick={() => handlePreviewMedia(record)}>
          <Tag>
            {getMediaIcon(text)} Preview
            {(text === "video" || text === "youtube") && record.duration_seconds != null
              ? ` (${formatDuration(record.duration_seconds)})`
              : ""}
          </Tag>
        </Link>
      ),
    },
    {
      title: "Orientation",
      dataIndex: "orientation",
      render: (text) =>
        text ? (
          <Tag color={text === "portrait" ? "blue" : "green"}>{capitalize(text)}</Tag>
        ) : (
          ""
        ),
    }];
  if (selectedStatus === "1") {
    columns.push(
      {
        title: "Times played",
        dataIndex: "total_play_count",
        sorter: true,
        render: (text) => (
          <span style={{ alignContent: "center" }}>
            {text ? text : 0}
          </span>
        ),
      }
    );
    columns.push({
      title: "Playlists",
      dataIndex: "playlist_count",
      render: (text) => (
        <span style={{ alignContent: "center" }}>
          {text ? text : 0}
        </span>
      ),
    });
  };
  columns.push({
    title: "File Size",
    dataIndex: "file_data",
    sorter: true,
    render: (text, record) => {
      let size = "-";
      if (record.file_data) {
        const fileData = JSON.parse(record.file_data);
        size = fileData.file_size;
        let len = fileData.file_size.length;
        fileData.file_size = fileData.file_size.substring(0, len - 3);
        if (parseFloat(fileData.file_size) > 1024) {
          const sz = parseFloat(fileData.file_size) / 1024;
          size = sz.toFixed(2) + " MB";
        }
      }
      return size;
    },
  });
  columns.push({
    title: "Optimization",
    dataIndex: "file_data",
    render: (text, record) => {
      if (!record.file_data) return "-";
      const fileData = JSON.parse(record.file_data);

      const original = Number(fileData.original_file_size);

      if (fileData.optimization_status === "pending") {
        return (
          <Tag color="default">
            Pending{original ? ` (${formatBytes(original)})` : ""}
          </Tag>
        );
      }
      if (fileData.optimization_status === "processing") {
        return (
          <Tag color="blue">
            Optimizing…{original ? ` (${formatBytes(original)})` : ""}
          </Tag>
        );
      }
      if (fileData.optimization_status === "failed") {
        return (
          <Tag color="red">
            Failed{original ? ` (${formatBytes(original)})` : ""}
          </Tag>
        );
      }

      const optimized = Number(fileData.optimized_file_size);
      // Only present once optimization has actually rewritten this file -
      // an item that was already optimal (nothing to rewrite) has neither
      // field even though its status is "optimized".
      if (!original || !optimized) return "-";
      const savingsPct = (((original - optimized) / original) * 100).toFixed(1);
      return (
        <Tooltip title={`${formatBytes(original)} → ${formatBytes(optimized)}`}>
          <span className="text-small">-{savingsPct}%</span>
        </Tooltip>
      );
    },
  });
  columns.push({
    title: "Status",
    dataIndex: "status",
    render: (status) => (
      <Tag color={status === "active" ? "green" : "red"}>
        {capitalize(status)}
      </Tag>
    ),
  })
  if (selectedStatus === "1") {
    columns.push({
      title: "Approved At",
      dataIndex: "approved_at",
      render: (text) => (
        <span className="text-small">
          {dayjs(text).format("DD-MM-YYYY HH:mm")}
        </span>
      ),
    });
    columns.push({
      title: "Approved By",
      dataIndex: "approved_by_user",
      render: (text) => {
        return <span className="text-small">{startCase(text)}</span>;
      },
    });
  }
  if (selectedStatus === "0") {
    columns.push({
      title: "Approval List",
      dataIndex: "approver_ids",
      render: (text, record) => {
        const len = record.approver_ids.length;
        if (len === 0) {
          return <Tag color="red">No Approver Assigned</Tag>;
        }

        if (len === 1) {
          return <Tag color="green">{record.approver_ids[0].name}</Tag>;
        }
        const sortedApprovers = [...record.approver_ids];
        return (
          <Popover
            placement="right"
            content={
              <Table
                loading={loading}
                columns={popOverColumns}
                dataSource={sortedApprovers}
                size="small"
                pagination={false}
                scroll={{ y: 400 }}
                style={{ width: "300px" }}
                rowKey="id"
              />
            }
            title="Assigned Approver List"
            trigger="hover"
          >
            <Tag style={{ cursor: "pointer" }} color="blue">
              {`${len} Approvers`}
            </Tag>
          </Popover>
        );
      },
    });
  }
  if (selectedStatus === "1" || selectedStatus === "0") {
    columns.push({
      title: "Updated At",
      dataIndex: "modified_at",
      render: (text) => (
        <span className="text-small">
          {dayjs(text).format("DD-MM-YYYY HH:mm")}
        </span>
      ),
    });
    columns.push({
      title: "Updated By",
      dataIndex: "updated_by_user",
      render: (text) => {
        return <span className="text-small">{startCase(text)}</span>;
      },
    });
  }
  if (selectedStatus === "-1") {
    columns.push({
      title: "Rejected At",
      dataIndex: "rejected_at",
      render: (text) => (
        <span className="text-small">
          {dayjs(text).format("DD-MM-YYYY HH:mm")}
        </span>
      ),
    });
    columns.push({
      title: "Rejected By",
      dataIndex: "rejected_by_user",
      render: (text) => {
        return <span className="text-small">{startCase(text)}</span>;
      },
    });
    columns.push({
      title: "Reject Reason",
      dataIndex: "reject_reason",
      render: (text) => {
        return <span className="text-small">{text}</span>;
      },
    });
  }

  if (editPermission || deletePermission || approvePermission) {
    columns.push({
      title: "Actions",
      render: (_, record) => (
        <Space>
          {user && (
            <>
              {editPermission &&
                ["1", "-1"].includes(selectedStatus) &&
                ([1, 2].includes(user.role_id) || record.created_by === user.id) && (
                  <Tooltip title="Edit">
                    <Button
                      size="small"
                      icon={<EditOutlined />}
                      onClick={() => handleEdit(record)}
                    />
                  </Tooltip>
                )}
              {editPermission && isEligibleForManualOptimize(record) && (() => {
                const fileData = JSON.parse(record.file_data);
                const isProcessing =
                  fileData.optimization_status === "processing" ||
                  optimizingId === record.id;
                return (
                  <Tooltip
                    title={
                      fileData.optimization_status === "processing"
                        ? "Optimization already in progress"
                        : "Optimize Media"
                    }
                  >
                    <Button
                      size="small"
                      icon={<SyncOutlined spin={optimizingId === record.id} />}
                      loading={optimizingId === record.id}
                      disabled={isProcessing}
                      onClick={() => handleOptimize(record.id)}
                    />
                  </Tooltip>
                );
              })()}
              {editPermission && isEligibleForYoutubeDurationCalc(record) && (
                <Tooltip title="Calculate Video Duration">
                  <Button
                    size="small"
                    icon={<ClockCircleOutlined spin={calculatingDurationId === record.id} />}
                    loading={calculatingDurationId === record.id}
                    onClick={() => handleCalculateYoutubeDuration(record.id, record.url_data)}
                  />
                </Tooltip>
              )}
              {deletePermission && ["1", "-1"].includes(selectedStatus) && (
                <Popconfirm
                  title="Are you sure to delete this media?"
                  onConfirm={() => handleDelete(record.id)}
                  okText="Yes"
                  cancelText="No"
                >
                  <Tooltip title="Delete">
                    <Button danger size="small" icon={<DeleteOutlined />} />
                  </Tooltip>
                </Popconfirm>
              )}
              {approvePermission && selectedStatus === "0" && (
                <>
                  <Popconfirm
                    title="Are you sure to approve this media?"
                    onConfirm={() => handleApprove(record.id)}
                    okText="Yes"
                    cancelText="No"
                  >
                    <Tooltip title="Approve Media">
                      <Button
                        size="small"
                        icon={<CheckOutlined />}
                        style={{
                          borderColor: "green",
                          color: "green",
                        }}
                      />
                    </Tooltip>
                  </Popconfirm>
                  <RejectButton record={record} handleReject={handleReject} />
                </>
              )}
            </>
          )}
        </Space>
      ),
    });
  }
  return (
    <RequirePermission permission="signage.media_view">
      <div style={{ padding: "1rem" }}>
        <Helmet>
          <title>{`${process.env.REACT_APP_NAME} | Media Library`}</title>
        </Helmet>
        <Title level={3} className="mb-3">
          Media Library
        </Title>

        <Radio.Group
          defaultValue={selectedStatus}
          buttonStyle="solid"
          className="mb-3"
          onChange={(e) => handleApprovalStatus(e)}
        >
          <Radio.Button value="1">Approved</Radio.Button>
          {approvePermission && (
            <Radio.Button value="0">
              Pending {pendingTotal > 0 && `(${pendingTotal})`}
            </Radio.Button>
          )}
          <Radio.Button value="-1">Rejected</Radio.Button>
        </Radio.Group>

        <div className="d-flex justify-content-between mb-3">
          <div className="d-flex gap-4">
            <Input.Search
              placeholder="Search media"
              value={filterText}
              onChange={handleSearch}
              style={{ width: 200 }}
            />

            <Button
              icon={<FilterOutlined />}
              type={showFilters ? "primary" : "default"}
              onClick={() => setShowFilters((prev) => !prev)}
            >
              Filters
              {(activeStatus || selectedType || selectedOrientation) && (
                <Tag color="blue" style={{ marginLeft: 6, marginRight: 0 }}>
                  {[activeStatus, selectedType, selectedOrientation].filter(Boolean).length}
                </Tag>
              )}
            </Button>
          </div>
          {createPermission && (
            <div>
              <Button
                type="primary"
                onClick={() => {
                  setEditingMedia(null);
                  setIsModalOpen(true);
                }}
                style={{ marginRight: "1rem" }}
              >
                Add New Media
              </Button>
              {selectedStatus === "0" && (
                <Button
                  icon={<SyncOutlined />}
                  onClick={() => handleSync()}
                  loading={syncLoading}
                  disabled={syncLoading}
                  style={{ backgroundColor: "#0b59c0ff", color: "white" }}
                >
                  Sync
                </Button>
              )}
              {deletePermission && (
                <Button
                  icon={<CloudServerOutlined />}
                  onClick={handleScanOrphaned}
                  style={{ marginLeft: "1rem" }}
                >
                  Check S3 Storage
                </Button>
              )}
            </div>
          )}
        </div>

        {showFilters && (
          <div className="d-flex gap-4 mb-3">
            <div>
              <span className="me-2">Status:</span>
              <Select
                value={activeStatus}
                showSearch
                onChange={(e) => handleStatusChange(e)}
                style={{ width: 200 }}
                allowClear
              >
                <Option value="active">Active</Option>
                <Option value="inactive">Inactive</Option>
              </Select>
            </div>
            <div>
              <span className="me-2">Type:</span>
              <Select
                value={selectedType}
                showSearch
                onChange={(e) => handleTypeChange(e)}
                style={{ width: 200 }}
                allowClear
              >
                <Option value="youtube">Youtube</Option>
                <Option value="video">Video</Option>
                <Option value="url">URL</Option>
                <Option value="image">Image</Option>
              </Select>
            </div>
            <div>
              <span className="me-2">Orientation:</span>
              <Select
                value={selectedOrientation}
                onChange={(e) => handleOrientationChange(e)}
                style={{ width: 160 }}
                allowClear
              >
                <Option value="landscape">Landscape</Option>
                <Option value="portrait">Portrait</Option>
              </Select>
            </div>
            <div className="d-flex align-items-end">
              <Button onClick={handleClearFilters}>Clear Filters</Button>
            </div>
          </div>
        )}

        <Table
          loading={loading}
          columns={columns}
          dataSource={media}
          scroll={{ x: "max-content" }}
          rowKey={(record) => record.id}
          onChange={handleTableChange}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: totalRecords,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} media`,
            showSizeChanger: true, // Enables the page size selection dropdown
            pageSizeOptions: ["5", "10", "20", "50", "100"], // Options for the page size selection
          }}
        />
        {isModalOpen && (
          <MediaForm
            isModalOpen={isModalOpen}
            closeModal={() => setIsModalOpen(false)}
            editingMedia={editingMedia}
            handleUpdateList={() => fetchData()}
          />
        )}
        {isPreviewModalOpen && (
          <PreviewMedia
            isModalOpen={isPreviewModalOpen}
            closeModal={() => setIsPreviewModalOpen(false)}
            media={previewMedia}
          />
        )}
        {orphanedPreview && (
          <PreviewMedia
            isModalOpen={!!orphanedPreview}
            closeModal={() => setOrphanedPreview(null)}
            media={orphanedPreview}
          />
        )}
        <Modal
          title="S3 Storage Cleanup"
          open={isCleanupModalOpen}
          onCancel={() => setIsCleanupModalOpen(false)}
          width={800}
          footer={[
            <Button key="close" onClick={() => setIsCleanupModalOpen(false)}>
              Close
            </Button>,
            <Button
              key="delete-selected"
              danger
              disabled={selectedOrphanedKeys.length === 0}
              loading={cleanupDeleting}
              onClick={handleDeleteSelectedOrphaned}
            >
              Delete Selected ({selectedOrphanedKeys.length})
            </Button>,
            <Button
              key="delete"
              danger
              type="primary"
              disabled={!orphanedResult || orphanedResult.orphaned.length === 0}
              loading={cleanupDeleting}
              onClick={handleDeleteOrphaned}
            >
              Delete All
            </Button>,
          ]}
        >
          {orphanedResult && (
            <p className="text-small mb-3">
              Environment: <strong>{orphanedResult.prefix}</strong> — checked{" "}
              {orphanedResult.totalObjects} file(s) in S3, found{" "}
              <strong>{orphanedResult.orphanedCount}</strong> not referenced by any
              media record ({formatBytes(orphanedResult.orphanedBytes)} total).
            </p>
          )}
          {orphanedResult && orphanedResult.orphaned.length > 0 && (
            <div className="mb-2">
              <Button
                size="small"
                onClick={() =>
                  setSelectedOrphanedKeys(orphanedResult.orphaned.map((o) => o.key))
                }
              >
                Select All
              </Button>{" "}
              <Button size="small" onClick={() => setSelectedOrphanedKeys([])}>
                Clear Selection
              </Button>
            </div>
          )}
          <Table
            loading={cleanupScanning}
            dataSource={orphanedResult ? orphanedResult.orphaned : []}
            rowKey="key"
            size="small"
            tableLayout="fixed"
            scroll={{ x: 760 }}
            pagination={{
              current: orphanedPage,
              pageSize: orphanedPageSize,
              onChange: (page, pageSize) => {
                setOrphanedPage(page);
                setOrphanedPageSize(pageSize);
              },
            }}
            rowSelection={{
              selectedRowKeys: selectedOrphanedKeys,
              onChange: (keys) => setSelectedOrphanedKeys(keys),
            }}
            columns={[
              {
                title: "S3 Key",
                dataIndex: "key",
                ellipsis: true,
                render: (key) => (
                  <Tooltip title={key}>
                    <span>{key}</span>
                  </Tooltip>
                ),
              },
              {
                title: "Size",
                dataIndex: "size",
                width: 90,
                align: "right",
                render: (size) => formatBytes(size),
              },
              {
                title: "Last Modified",
                dataIndex: "lastModified",
                width: 150,
                render: (date) => (date ? new Date(date).toLocaleDateString() : "-"),
              },
              {
                title: "Preview",
                width: 70,
                align: "center",
                render: (_, record) =>
                  record.mediaType ? (
                    <Tooltip title="Preview">
                      <Button
                        size="small"
                        icon={<EyeOutlined />}
                        onClick={() =>
                          setOrphanedPreview({
                            title: record.key.split("/").pop(),
                            orientation: "landscape",
                            media_type: record.mediaType,
                            file_data: JSON.stringify({ file_path: record.previewUrl }),
                          })
                        }
                      />
                    </Tooltip>
                  ) : (
                    <span>-</span>
                  ),
              },
            ]}
          />
        </Modal>
      </div>
    </RequirePermission>
  );
};

export default Library;
